/**
 * Service-worker registration with clean update lifecycle.
 *
 * Behavior:
 *  - On every page load, register `/service-worker.js`.
 *  - When a new SW finishes installing and an old SW is still controlling the
 *    page, dispatch the `sw:update-available` window event with an `update()`
 *    callback. The app (see App.js) shows a non-blocking toast that lets the
 *    user choose when to reload. We never auto-reload mid-task.
 *  - Periodic update check every hour for long-lived tabs.
 */
export function registerServiceWorker() {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', async () => {
    let registration;
    try {
      registration = await navigator.serviceWorker.register('/service-worker.js');
    } catch {
      return; // SW registration failed — silently degrade to no-SW
    }

    // If there's already a waiting SW from a previous load, surface it now.
    if (registration.waiting && navigator.serviceWorker.controller) {
      announceUpdate(registration);
    }

    // Watch for new installs
    registration.addEventListener('updatefound', () => {
      const sw = registration.installing;
      if (!sw) return;
      sw.addEventListener('statechange', () => {
        if (sw.state === 'installed' && navigator.serviceWorker.controller) {
          announceUpdate(registration);
        }
      });
    });

    // Periodic update check (keeps long-running tabs fresh)
    setInterval(() => {
      registration.update().catch(() => { /* offline ok */ });
    }, 60 * 60 * 1000);
  });
}

function announceUpdate(registration) {
  const update = () => {
    if (!registration.waiting) return;
    // Reload once the new SW actually takes control
    let reloaded = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });
    registration.waiting.postMessage('SKIP_WAITING');
  };

  try {
    window.dispatchEvent(new CustomEvent('sw:update-available', { detail: { update } }));
  } catch { /* CustomEvent unavailable — noop */ }
}
